import React, { useState, useEffect } from 'react';
// import { Link } from // Removed unused import 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { apartmentsApi, type ApartmentGroup, type Building } from '../../api/apartmentsApi';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import AddApartmentGroupModal from '../../components/modals/AddApartmentGroupModal';
import BulkAssignApartmentsToGroupModal from '../../components/modals/BulkAssignApartmentsToGroupModal';

const ApartmentGroups: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignGroup, setAssignGroup] = useState<ApartmentGroup | null>(null);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadApartmentGroups();
    } else {
      setApartmentGroups([]);
      setLoading(false);
    }
  }, [selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (err) {
      console.error('Error loading buildings:', err);
      setError(t('failedToLoad'));
    }
  };

  const loadApartmentGroups = async () => {
    if (!selectedBuildingId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apartmentsApi.getApartmentGroups(selectedBuildingId);
      setApartmentGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading apartment groups:', err);
      setError(t('errorLoadingApartmentGroups'));
      setApartmentGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('confirmDeleteApartmentGroup'))) {
      try {
        await apartmentsApi.deleteApartmentGroup(id);
        setApartmentGroups(prev => (prev || []).filter(group => group.id !== id));
      } catch (err) {
        console.error('Error deleting apartment group:', err);
        setError(t('errorDeletingApartmentGroup'));
      }
    }
  };

  const handleGroupCreated = () => {
    loadApartmentGroups();
  };

  const handleBuildingChange = (buildingId: string) => {
    selectBuilding(user?.id, buildingId, setSelectedBuildingId);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleRecordsPerPageChange = (records: number) => {
    setRecordsPerPage(records);
    setCurrentPage(1);
  };

  // Filter and paginate apartment groups
  const filteredGroups = (apartmentGroups || []).filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalRecords = filteredGroups.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentGroups = filteredGroups.slice(startIndex, endIndex);

  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('loadingApartmentGroups')}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('apartmentGroups')}</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          {t('addApartmentGroup')}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Building Selection */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {shouldShowBuildingSelector(buildings) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('selectBuilding')}
              </label>
              <select
                value={selectedBuildingId}
                onChange={(e) => handleBuildingChange(e.target.value)}
                disabled={!needsBuildingSelection(buildings)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('selectBuildingPlaceholder')}</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedBuilding && (
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <div><span className="font-medium">{t('operator')}:</span> {selectedBuilding.operator?.name}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {!selectedBuildingId ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">{t('pleaseSelectBuildingToViewApartmentGroups')}</div>
        </div>
      ) : !apartmentGroups || apartmentGroups.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">{t('noApartmentGroups')}</div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            {t('addFirstApartmentGroup')}
          </button>
        </div>
      ) : (
        <>
          {/* Search and Records Per Page */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex-1 max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('search')}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{t('recordsPerPage')}:</span>
              <select
                value={recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">{apartmentGroups.length}</div>
              <div className="text-gray-600">{t('totalGroups')}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-green-600">
                {apartmentGroups.filter(group => group.status === 'ACTIVE').length}
              </div>
              <div className="text-gray-600">{t('active')}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-orange-600">
                {apartmentGroups.reduce((sum, group) => sum + (group.apartmentCount || group._count?.apartments || 0), 0)}
              </div>
              <div className="text-gray-600">{t('totalApartments')}</div>
            </div>
          </div>

          {/* Pagination Info */}
          {totalRecords > 0 && (
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-600">
                {t('showingRecords').replace('{from}', (startIndex + 1).toString()).replace('{to}', Math.min(endIndex, totalRecords).toString()).replace('{total}', totalRecords.toString())}
              </div>
              <div className="text-sm text-gray-600">
                {t('page')} {currentPage} {t('of')} {totalPages}
              </div>
            </div>
          )}

          {/* Groups Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('stt')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('groupName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('description')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('apartmentCount')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentGroups.map((group, index) => (
                    <tr key={group.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-blue-600">
                          {group.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {group.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {group.apartmentCount || group._count?.apartments || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          group.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {group.status === 'ACTIVE' ? t('active') : t('inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => setAssignGroup(group)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          {t('assignApartments')}
                        </button>
                        <button
                          onClick={() => handleDelete(group.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                        >
                          {t('delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('first')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('previous')}
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = currentPage - 2 + i;
                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('last')}
              </button>
            </div>
          )}

          {filteredGroups.length === 0 && searchTerm && (
            <div className="text-center py-8 text-gray-500">
              {t('noData')}
            </div>
          )}
        </>
      )}

      <AddApartmentGroupModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleGroupCreated}
        selectedBuildingId={selectedBuildingId}
      />

      <BulkAssignApartmentsToGroupModal
        isOpen={!!assignGroup}
        onClose={() => setAssignGroup(null)}
        onSuccess={loadApartmentGroups}
        group={assignGroup}
        buildingId={selectedBuildingId}
      />
    </div>
  );
};

export default ApartmentGroups; 